
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Lightbulb, Target, Video, Smartphone, CheckCircle, Star } from 'lucide-react';

const SolutionIntroductionSection = () => {
  const { isMobile } = useBreakpoint();

  const features = [
    {
      icon: Target,
      title: "Professional Landing Page",
      description: "A complete, high-converting sales page you can customize with your branding and use immediately. No design skills needed. No copywriting required. Just Swipe and Deploy!"
    },
    {
      icon: Video,
      title: "White Label Video Sales Letter",
      description: "Multiple professionally produced video versions that explain the AI business card service to your prospects. Plus the complete script. These videos do the selling FOR you."
    },
    {
      icon: Smartphone,
      title: "6 Industry-Specific Templates", 
      description: "Pre-built AI business card designs for real estate agents, contractors, insurance agents, restaurants, consultants, and service providers."
    }
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-[#003a70]/5 via-white to-[#FEE6D8]/20" 
      paddingY="py-16 md:py-20 lg:py-24"
      containerWidth="wide"
      backgroundPattern="subtle-grid"
      patternColor="#003a70"
      patternOpacity={2}
    >
      <div className="text-center space-y-12 md:space-y-16">
        
        {/* Question Hook */}
        <div className="space-y-8 animate-fade-in">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-[#003a70]/10 to-[#FEE6D8]/30 rounded-full shadow-lg border-2 border-[#003a70]/20">
            <Lightbulb className="text-[#003a70]" size={isMobile ? 24 : 28} />
            <span className="text-[#003a70] font-bold text-lg md:text-xl">What If...</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl md:text-3xl" : "text-3xl lg:text-4xl xl:text-5xl"
          )}>
            What If You Could Skip All That and 
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">Start Selling Tomorrow?</span>
          </h2>
          
          <p className={cn(
            "text-[#003a70]/90 leading-relaxed max-w-3xl mx-auto",
            isMobile ? "text-lg" : "text-xl lg:text-2xl"
          )}>
            What if you had everything you needed - professionally created, ready to deploy - waiting for you right now?
          </p>
        </div>

        {/* Solution Introduction */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="relative bg-white rounded-3xl p-8 md:p-12 border-2 border-[#FEE6D8]/60 shadow-2xl max-w-5xl mx-auto overflow-hidden">
            {/* Decorative gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/10 via-transparent to-[#003a70]/5 pointer-events-none"></div>
            
            {/* Success accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#003a70] to-[#003a70]/80"></div>
            
            <div className="relative z-10 text-center space-y-6">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-full flex items-center justify-center shadow-lg">
                  <Star className="text-white" size={28} />
                </div>
              </div>
              
              <h3 className={cn(
                "font-bold text-[#003a70] leading-tight",
                isMobile ? "text-xl md:text-2xl" : "text-2xl lg:text-3xl"
              )}>
                Introducing: The Complete 
                <br />
                <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">
                  Genius Card Playbook DFY Bundle
                </span>
              </h3>
              
              <p className={cn(
                "text-[#003a70]/90 font-semibold leading-relaxed",
                isMobile ? "text-lg" : "text-xl"
              )}>
                Here's exactly what you get:
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="space-y-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="grid gap-8 md:gap-10 max-w-6xl mx-auto">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div 
                  key={index}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-[#FEE6D8]/40 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#003a70] to-[#003a70]/80 rounded-xl flex items-center justify-center shadow-lg">
                        <IconComponent className="text-white" size={28} />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 text-left">
                      <div className="flex items-start gap-3 mb-4">
                        <div className="text-2xl" role="img" aria-label="Target">🎯</div>
                        <h4 className={cn(
                          "font-bold text-[#003a70] leading-tight",
                          isMobile ? "text-lg md:text-xl" : "text-xl lg:text-2xl"
                        )}>
                          {feature.title}
                        </h4>
                      </div>
                      
                      <p className={cn(
                        "text-[#003a70]/90 leading-relaxed",
                        isMobile ? "text-base" : "text-lg"
                      )}>
                        {feature.description}
                      </p>
                      
                      {/* Checkmark for emphasis */}
                      <div className="flex items-center gap-2 mt-4">
                        <CheckCircle className="text-[#003a70]" size={20} />
                        <span className="text-[#003a70] font-semibold text-sm md:text-base">
                          Ready to use immediately
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Emphasis Statement */}
        <div className="space-y-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/30 via-white to-[#003a70]/5 rounded-2xl p-8 md:p-12 border border-[#FEE6D8]/50 shadow-lg max-w-4xl mx-auto">
            <div className="flex justify-center items-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className="text-[#003a70] fill-[#003a70]" 
                  size={isMobile ? 16 : 20} 
                />
              ))}
            </div>
            <p className={cn(
              "text-[#003a70] font-bold leading-relaxed",
              isMobile ? "text-lg md:text-xl" : "text-xl lg:text-2xl"
            )}>
              Everything professionally created, tested, and ready to deploy.
              <br />
              <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">No guesswork. No delays. Just results.</span>
            </p>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default SolutionIntroductionSection;
